import dayjs from 'dayjs'
import React from 'react'
import { useForm, SubmitHandler } from 'react-hook-form'

import { getIsValidIban } from '../../../api/senecApiUtils'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { setCustomerValuesCustomerSlice } from '../../../store/slices/customerInputValuesSlice'
import { customerValuesType } from '../../../types'
import {
  AddressWithValidation,
  FormCheck,
  FormDate,
  FormInput,
  FormRadioItem
} from '../../molecules'

type CustomerProps = {
  nextPage: string,
  prevPage:string,
  changeActivePage: (pageUrl: string) => void
}

const Component = ({ nextPage, prevPage, changeActivePage }: CustomerProps) => {
  const customerInputValues = useAppSelector(state => state.customervalues)
  const dispatch = useAppDispatch()

  const defaultFormValues = { ...customerInputValues['p-customer'] }
  defaultFormValues['customer-strasse'] = customerInputValues['p-customer']['customer-strasse']
  defaultFormValues['customer-hausnummer'] = customerInputValues['p-customer']['customer-hausnummer']
  defaultFormValues['customer-postleitzahl'] = customerInputValues['p-intro']['customer-postleitzahl']
  defaultFormValues['customer-ort'] = customerInputValues['p-customer']['customer-ort']

  const { register, handleSubmit, watch, control, getValues, setValue, formState: { errors }, clearErrors } = useForm<customerValuesType.Customer>({
    mode: 'onChange',
    defaultValues: defaultFormValues
  })
  const watchAdditionalInputs = watch('customer-type')
  const watchPaymentMethod = watch('customer-zahlungsweise')
  const watchDifferentInvoicingAddress = watch('customer-rechnungsadresse-abweichend')

  const validateIban = (code: string) => {
    if (code) {
      if (code.replace(/ /g, '').length === 22) {
        return getIsValidIban(code)
          .then(data => {
            if (data) {
              const dataObject = JSON.parse(data)
              if (
                getValues('customer-bankverbindung-bic') !==
                dataObject.bic ||
                getValues('customer-bankverbindung-geldinstitut') !==
                dataObject.bankname
              ) {
                setValue('customer-bankverbindung-bic', dataObject.bic)
                setValue('customer-bankverbindung-geldinstitut', dataObject.bankname)
              }
              return dataObject.valid
            }
            return null
          })
          .catch(error => {
            // eslint-disable-next-line no-console
            console.log(`error validating code: ${error}`)
          })
      }
      return false
    }
    return true
  }

  const handleBack = () => {
    const formData = getValues()
    dispatch(setCustomerValuesCustomerSlice(formData))
    changeActivePage(prevPage)
  }

  const onSubmitFn: SubmitHandler<customerValuesType.Customer> = data => {
    dispatch(setCustomerValuesCustomerSlice(data))
    changeActivePage(nextPage)
  }

  return (
    <div className="react-form__page react-form__page--customer">
      <div className="react-form__page__header relative bg-light pt-10">
        <div className="react-form__page__intro-text text-center pb-10">
          <h1 className="h2">Ihre Kundendaten</h1>
          <p>
            Wir freuen uns, dass Sie sich für unser Angebot entschieden haben.
          </p>
          <p>
            Im nächsten Schritt benötigen wir Ihre Daten, um Ihren Auftrag zu bearbeiten.
          </p>
        </div>
      </div>
      <div className="react-form__page--customer__body">
        <form>
          <div className="bg-gray-lighter p-10">
            <h2 className="h3 mb-10">Vertragsinhaber</h2>
            <div className="pb-6">
              <div className="grid grid-cols-2 md:grid-cols-4 md:gap-4">
                <FormRadioItem
                  name="customer-type"
                  id="customer-type-privat"
                  value="0"
                  label="Privat"
                  className="inline-block"
                  register={register}
                  errors={errors}
                  defaultChecked={customerInputValues['p-customer']['customer-type'] === '0'}
                  validations={{
                    required: 'Bitte füllen Sie dieses Feld aus.'
                  }}
                />
                <FormRadioItem
                  name="customer-type"
                  id="customer-type-firma"
                  value="1"
                  label="Firma"
                  className="inline-block"
                  register={register}
                  errors={errors}
                  defaultChecked={customerInputValues['p-customer']['customer-type'] === '1'}
                  validations={{
                    required: 'Bitte füllen Sie dieses Feld aus.'
                  }}
                />
              </div>
              {watchAdditionalInputs === '1' && (
                <FormInput
                  id="customer-firmenname"
                  label="Firmenname"
                  placeholder="Firmenname"
                  className="w-full sm:w-1/2"
                  errors={errors}
                  register={register}
                  validations={{
                    required: 'Bitte füllen Sie dieses Feld aus.'
                  }}
                />
              )}
            </div>
            <div className="flex flex-wrap">
              <div className="w-full">
                <div className="flex flex-wrap items-baseline">
                  <div className="w-full">
                      <div className="grid grid-cols-2 md:grid-cols-4 md:gap-4">
                        <FormRadioItem
                          name="customer-anrede"
                            id="customer-anrede-herr"
                            value="Herr"
                            label="Herr"
                            className="inline-block"
                            register={register}
                            errors={errors}
                            defaultChecked={customerInputValues['p-customer']['customer-anrede'] === 'Herr'}
                            validations={{
                              required: 'Bitte füllen Sie dieses Feld aus.'
                            }}
                          />
                          <FormRadioItem
                            name="customer-anrede"
                            id="customer-anrede-frau"
                            value="Frau"
                            label="Frau"
                            className="inline-block"
                            register={register}
                            errors={errors}
                            defaultChecked={customerInputValues['p-customer']['customer-anrede'] === 'Frau'}
                            validations={{
                              required: 'Bitte füllen Sie dieses Feld aus.'
                            }}
                          />
                          <FormRadioItem
                            name="customer-anrede"
                            id="customer-anrede-divers"
                            value="Divers"
                            label="Divers"
                            className="inline-block"
                            register={register}
                            errors={errors}
                            defaultChecked={customerInputValues['p-customer']['customer-anrede'] === 'Divers'}
                            validations={{
                              required: 'Bitte füllen Sie dieses Feld aus.'
                            }}
                          />
                          <FormRadioItem
                            name="customer-anrede"
                            id="customer-anrede-keine-angabe"
                            value="Keine Angabe"
                            label="Keine Angabe"
                            className="inline-block"
                            register={register}
                            errors={errors}
                            defaultChecked={customerInputValues['p-customer']['customer-anrede'] === 'Keine Angabe'}
                            validations={{
                              required: 'Bitte füllen Sie dieses Feld aus.'
                            }}
                          />
                      </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap -mx-4">
              <FormInput
                id="customer-vorname"
                label="Vorname"
                placeholder="Vorname"
                className="w-full sm:w-1/2 px-4"
                errors={errors}
                register={register}
                validations={{
                  required: 'Bitte füllen Sie dieses Feld aus.',
                  pattern: {
                    value: /^[a-zA-Z0-9äöüÄÖÜß]+[a-zA-Z0-9äöüÄÖÜß\-\s]*[a-zA-Z0-9äöüÄÖÜß]+$/,
                    message: 'Bitte geben Sie den Vornamen ohne Leerzeichen (auch am Anfang und am Ende) ein.'
                  }
                }}
              />
              <FormInput
                id="customer-nachname"
                label="Name"
                placeholder="Name"
                className="w-full sm:w-1/2 px-4"
                errors={errors}
                register={register}
                validations={{
                  required: 'Bitte füllen Sie dieses Feld aus.',
                  pattern: {
                    value: /^[a-zA-Z0-9äöüÄÖÜß]+[a-zA-Z0-9äöüÄÖÜß\-\s]*[a-zA-Z0-9äöüÄÖÜß]+$/,
                    message: 'Bitte geben Sie den Vornamen ohne Leerzeichen (auch am Anfang und am Ende) ein.'
                  }
                }}
              />
            </div>
          <div className="w-full">
            <div className="flex flex-wrap -mx-4">
            <FormDate
              errors={errors}
              register={register}
              control={control}
              id="customer-geburtstag"
              label="Geburtstag"
              className="w-full sm:w-1/2 lg:w-3/12 px-4"
              peekNextMonth={true}
              showMonthDropdown={true}
              showYearDropdown={true}
              dropdownMode="select"
              validations={{
                required: 'Bitte füllen Sie dieses Feld aus.',
                validate: value => {
                  return dayjs(value).isBefore(dayjs().subtract(18, 'year')) || 'Sie müssen mindestens 18 Jahre alt sein, um einen rechtsgültigen Vertrag abschließen zu können.'
                }
              }}
              maxDate={new Date()}
            />
            <FormInput
              id="customer-telefonnummer"
              label="Telefon"
              placeholder="Telefon"
              className="w-full sm:w-1/2 lg:w-3/12 px-4"
              errors={errors}
              register={register}
              validations={{
                pattern: {
                  value: /^[\d-|/\s]+$/,
                  message: 'Bitte geben Sie für die Telefonnummer nur Zahlen ein.'
                }
              }}
            />
            <FormInput
              id="customer-emailAddresse"
              label="E-Mail-Adresse"
              placeholder="E-Mail-Adresse"
              inputType="email"
              className="w-full sm:w-1/2 lg:w-3/12 px-4"
              errors={errors}
              register={register}
              validations={{
                required: 'Bitte füllen Sie dieses Feld aus.',
                pattern: {
                  value: /^(([^<>()\]\\.,;:\s@"]+(\.[^<>()\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                  message: 'Die E-Mail-Adresse ist nicht gültig.'
                }
              }}
            />
            <FormInput
              id="customer-email-confirmation"
              label="E-Mail-Adresse bestätigen"
              placeholder="E-Mail-Adresse bestätigen"
              className="w-full sm:w-1/2 lg:w-3/12 px-4"
              inputType="email"
              errors={errors}
              register={register}
              validations={{
                required: 'Bitte füllen Sie dieses Feld aus.',
                pattern: {
                  value: /^(([^<>()\]\\.,;:\s@"]+(\.[^<>()\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                  message: 'Die E-Mail Adresse ist nicht gültig.'
                },
                validate: (value: string) => value === getValues('customer-emailAddresse') || 'Die E-Mails sind nicht gleich.'
              }}
              onPaste={(e) => {
                e.preventDefault()
                return false
              }}
            />
            </div>
          </div>
            <div className="w-full">
              <h2 className="h3 mb-10">Lieferanschrift</h2>
                  <AddressWithValidation
                    sliceName="introAddress"
                    prefix="customer"
                    errors={errors}
                    register={register}
                    watch={watch}
                    control={control}
                    clearErrors={clearErrors}
                    setValue={setValue}
                    initialPostalCode={customerInputValues['p-intro']['customer-postleitzahl'] ? customerInputValues['p-intro']['customer-postleitzahl'] : ''}
                    cityOptionsInitial={typeof customerInputValues['p-customer']['customer-ort'] === 'object' ? [customerInputValues['p-customer']['customer-ort']] : []}
                    streetOptionsInitial={typeof customerInputValues['p-customer']['customer-strasse'] === 'object' ? [customerInputValues['p-customer']['customer-strasse']] : []}
                  />
              </div>
            </div>
          <div className="bg-gray-light p-10">
            <h2 className="h3 mb-10">Abweichende Rechnungsadresse</h2>
            <FormCheck
              id="customer-rechnungsadresse-abweichend"
              label="Ich möchte die Rechnung an eine andere Adresse erhalten"
              className="w-full sm:w-1/2 mt-4 md:mt-0"
              register={register}
              errors={errors}
            />
            {watchDifferentInvoicingAddress && (
              <AddressWithValidation
                sliceName="customerAddressAbw"
                prefix="customer"
                suffix="abw"
                errors={errors}
                register={register}
                watch={watch}
                control={control}
                clearErrors={clearErrors}
                setValue={setValue}
                cityOptionsInitial={typeof customerInputValues['p-customer']['customer-ort-abw'] === 'object' ? [customerInputValues['p-customer']['customer-ort-abw']] : []}
                streetOptionsInitial={typeof customerInputValues['p-customer']['customer-strasse-abw'] === 'object' ? [customerInputValues['p-customer']['customer-strasse-abw']] : []}
              />
            )}
          </div>
          <div className="bg-gray-lighter p-10">
            <h2 className="h3 mb-10">Kontoverbindung</h2>
            <div className="react-form__form-group form-group form-group--no-outline">
              <div>
                <FormRadioItem
                  name="customer-zahlungsweise"
                  id="customer-zahlungsweise-ueberweisung"
                  value="customer-zahlungsweise-ueberweisung"
                  label="per Überweisung mit Terminstellung bis zum 1. des Monats"
                  defaultChecked={customerInputValues['p-customer']['customer-zahlungsweise'] === 'customer-zahlungsweise-ueberweisung'}
                  register={register}
                  errors={errors}
                />
                <FormRadioItem
                  name="customer-zahlungsweise"
                  id="customer-zahlungsweise-lastschrift"
                  label="per Einzugsermächtigung und SEPA-Lastschriftmandat"
                  value="customer-zahlungsweise-lastschrift"
                  defaultChecked={customerInputValues['p-customer']['customer-zahlungsweise'] === 'customer-zahlungsweise-lastschrift'}
                  register={register}
                  errors={errors}
                />
              </div>
            </div>
            {watchPaymentMethod ===
              'customer-zahlungsweise-lastschrift' && (
                <>
                  <div className="flex flex-wrap -mx-4">
                    <FormInput
                      id="customer-bankverbindung-vorname"
                      label="Vorname des Kontoinhabers"
                      className="w-full sm:w-1/2 px-4"
                      placeholder="Vorname des Kontoinhabers"
                      errors={errors}
                      register={register}
                      validations={{
                        required: 'Bitte füllen Sie dieses Feld aus.'
                      }}
                    />
                    <FormInput
                      id="customer-bankverbindung-nachname"
                      label="Name des Kontoinhabers"
                      className="w-full sm:w-1/2 px-4"
                      placeholder="Name des Kontoinhabers"
                      errors={errors}
                      register={register}
                      validations={{
                        required: 'Bitte füllen Sie dieses Feld aus.'
                      }}
                    />
                    <FormInput
                      id="customer-bankverbindung-iban"
                      label="IBAN"
                      className="w-full sm:w-1/2 px-4"
                      placeholder="IBAN"
                      errors={errors}
                      register={register}
                      validations={{
                        required: 'Bitte füllen Sie dieses Feld aus.',
                        validate: (code: string) => validateIban(code) || 'The IBAN is not valid'
                      }}
                    />
                    <FormInput
                      id="customer-bankverbindung-bic"
                      label="BIC"
                      disabled={true}
                      className="w-full sm:w-1/2 px-4"
                      placeholder="BIC"
                      errors={errors}
                      register={register}
                      validations={{
                        required: 'Bitte füllen Sie dieses Feld aus.'
                      }}
                    />
                    <FormInput
                      id="customer-bankverbindung-geldinstitut"
                      label="Geldinstitut"
                      disabled={true}
                      className="w-full sm:w-1/2 px-4"
                      placeholder="Geldinstitut"
                      errors={errors}
                      register={register}
                      validations={{
                        required: 'Bitte füllen Sie dieses Feld aus.'
                      }}
                    />
                  </div>
                  <div className="flex flex-wrap">
                    <div className="react-form__form-group form-group form-group--no-outline w-full">
                      <FormCheck
                        id="customer-iban-mandatory"
                        label="Ja, ich ermächtige die SENEC GmbH, Zahlungen von meinem Konto mittels Lastschrift
                          einzuziehen. Zugleich weise ich mein Kreditinstitut an, die von der SENEC GmbH auf mein Konto
                          gezogenen Lastschriften einzulösen. (Gläubiger-Identifikationsnummer DE27ZZZ00001552938,
                          Mandatsreferenz wird separat mitgeteilt"
                        register={register}
                        errors={errors}
                        validations={{
                          required: 'Bitte füllen Sie dieses Feld aus.'
                        }}
                      />
                    </div>
                  </div>
                </>
            )}
          </div>
          <div className="clearfix bg-gray-lighter px-5 pb-10">
            {prevPage && (
              <qz-button
                class="float-left"
                onClick={handleBack}
              >
                Zurück
              </qz-button>
            )}
            {nextPage && (
              <qz-button
                class="float-right"
                onClick={handleSubmit(onSubmitFn)}
                buttonType="submit"
              >
                Weiter
              </qz-button>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}

export default Component
